import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import useApiRequest from "../../lib/hooks/useRequest";
import { ApplicantLogMailResponseDto } from "../../lib/interfaces/applicant";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/components/ui/dialog";

interface MailLogDialogProps {
  applicantId: string;
  logId: string;
  isOpen: boolean;
  onClose: () => void;
}

const MailLogDialog = ({ applicantId, logId, isOpen, onClose }: MailLogDialogProps) => {
  const { apiRequest } = useApiRequest();
  const [mailLog, setMailLog] = useState<ApplicantLogMailResponseDto | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchMailLog = useCallback(async () => {
    if (!logId || !applicantId) return;

    setIsLoading(true);
    const response = await apiRequest<ApplicantLogMailResponseDto>(
      `applicants/${applicantId}/logs/mail/${logId}`,
      "GET"
    );
    if (response.data) {
      setMailLog(response.data);
    }
    setIsLoading(false);
  }, [apiRequest, applicantId, logId]);

  useEffect(() => {
    if (isOpen && logId) {
      fetchMailLog();
    }
  }, [isOpen, logId, fetchMailLog]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
      setMailLog(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="min-w-[650px]">
        <DialogHeader>
          <DialogTitle>Gesendete E-Mail</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <Flex justify="center" align="center" py={8}>
            <Spinner size="lg" />
          </Flex>
        ) : mailLog ? (
          <Box className="space-y-4">
            <Box className="grid grid-cols-[120px_1fr] gap-2 text-sm">
              <Text className="font-semibold text-gray-600">Gesendet am:</Text>
              <Text>{format(new Date(mailLog.createdAt), "dd.MM.yyyy HH:mm")}</Text>

              <Text className="font-semibold text-gray-600">Gesendet von:</Text>
              <Text>{mailLog.userName}</Text>

              <Text className="font-semibold text-gray-600">Von:</Text>
              <Text>
                {mailLog.fromName} &lt;{mailLog.fromAddressPrefix}@kandidat.io&gt;
              </Text>

              <Text className="font-semibold text-gray-600">Antwort an:</Text>
              <Text>
                {mailLog.replyToName} &lt;{mailLog.replyToAddress}&gt;
              </Text>

              <Text className="font-semibold text-gray-600">An:</Text>
              <Text>{mailLog.to}</Text>

              <Text className="font-semibold text-gray-600">Betreff:</Text>
              <Text className="font-medium">{mailLog.subject}</Text>
            </Box>

            <Box>
              <Text className="font-semibold text-gray-600 mb-2">Inhalt:</Text>
              <Box
                className="border rounded-md p-4 bg-gray-50 max-h-[300px] overflow-y-auto"
                dangerouslySetInnerHTML={{ __html: mailLog.text }}
              />
            </Box>
          </Box>
        ) : (
          <Text className="text-center py-4 text-gray-500">Keine Daten verfügbar</Text>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MailLogDialog;
